/**
 * §P.8 · Quién ha firmado qué.
 *
 * ## Abre por «a quién le falta», y no por la tabla completa
 *
 * `BFF_BO.md` §7.6 lo dice de una línea: «`GET /consents/missing` es una lista
 * **accionable**: a quién le falta firmar qué». Una tabla de ciento veinte alumnos
 * por ocho finalidades es un dato; una lista de cuatro nombres es una tarea que se
 * termina esta tarde.
 *
 * ## «Sin preguntar» no es «dijo que no»
 *
 * Es el fallo que esta pantalla existe para no cometer. Pintar en rojo un permiso
 * que nadie pidió le dice al club que la familia se negó, y entonces no se lo pide
 * nunca — porque ya cree tener la respuesta. Va en ámbar y con esas palabras.
 *
 * ## Desde aquí no se firma
 *
 * El consentimiento lo da la familia desde su app, con su nombre y su hora. Un
 * botón aquí para marcarlo «por ellos» convertiría la tabla en papel mojado: lo que
 * la hace defendible es justo que el club no la pueda rellenar. Lo que sí se puede
 * es **avisar**, y para eso está §P.6.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import {
  ESTADOS_DE_CONSENTIMIENTO,
  estadoDeConsentimiento,
  nombreDeAlumno,
  nombreDeQuienFirmo,
  type ConsentimientosDeAlumno,
} from '@/club/cumplimiento/tipos';

export function Consentimientos({
  club,
  alAbrirAlumno,
  alAvisar,
}: {
  club: string;
  alAbrirAlumno: (alumnoId: string) => void;
  /** Lleva a §P.6 a escribir el aviso. Es la única acción que cabe desde aquí. */
  alAvisar: () => void;
}) {
  const [soloLosQueFaltan, setSoloLosQueFaltan] = useState(true);
  const [abierto, setAbierto] = useState<string | null>(null);

  const lista = useQuery({
    queryKey: ['consentimientos', club, soloLosQueFaltan],
    queryFn: async () => {
      /*
       * Las dos cadenas enteras, sin sacar el tronco a una variable. Escrito así
       * a propósito: `contract:frontend` busca la cadena de Eden seguida, y
       * partida en un `const` daba «GET /consents/missing sin consumir» en cada
       * ejecución. Una falsa alarma permanente en un informe enseña a no leerlo.
       * 18-09-2026.
       */
      const { data, error } = soloLosQueFaltan
        ? await api.v1.clubs({ club }).consents.missing.get({
            query: { limit: 500 },
          })
        : await api.v1.clubs({ club }).consents.get({ query: { limit: 500 } });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as ConsentimientosDeAlumno[];
    },
  });

  return (
    <section>
      <header className="titulo-fila">
        <h1>Consentimientos</h1>
        <nav className="pestañas" aria-label="Qué alumnos se enseñan">
          <button
            type="button"
            className={soloLosQueFaltan ? 'pestaña-activa' : 'btn-plano'}
            onClick={() => setSoloLosQueFaltan(true)}
          >
            A quién le falta
          </button>
          <button
            type="button"
            className={soloLosQueFaltan ? 'btn-plano' : 'pestaña-activa'}
            onClick={() => setSoloLosQueFaltan(false)}
          >
            Todos
          </button>
        </nav>
      </header>

      <p className="tenue">
        Ocho permisos por alumno, cada uno por separado y revocable: nunca hay
        un «acepto todo». Los firma la familia desde su app — desde aquí se ve
        quién falta, no se marca por ellos.
      </p>

      {lista.isPending && (
        <Cargando filas={4} etiqueta="Cargando los consentimientos" />
      )}

      {lista.isError && (
        <PanelError
          problema={aProblema(lista.error)}
          reintentar={() => void lista.refetch()}
        />
      )}

      {lista.data?.length === 0 &&
        (soloLosQueFaltan ? (
          <Vacio
            titulo="No falta ninguno"
            explicacion="Todos los alumnos tienen firmados los permisos obligatorios. Los opcionales pueden faltar y no pasa nada: son opcionales."
            accion={
              <button type="button" onClick={() => setSoloLosQueFaltan(false)}>
                Ver la tabla completa
              </button>
            }
          />
        ) : (
          <Vacio
            titulo="Todavía no hay alumnos"
            explicacion="Los permisos son de cada alumno, así que esta tabla se llena según se dan de alta."
          />
        ))}

      {lista.data && lista.data.length > 0 && (
        <ul className="consentimientos">
          {lista.data.map((a) => {
            const faltan = a.missing_required;
            // Del código al nombre legible, con lo que ya vino en la misma fila: la
            // lista de finalidades es del servidor y copiarla aquí sería tenerla dos
            // veces, desincronizada en cuanto añada la novena.
            const nombreDe = (codigo: string) =>
              a.consents.find((c) => c.code === codigo)?.label ?? codigo;
            const desplegado = abierto === a.student_id;

            return (
              <li key={a.student_id}>
                <div className="aviso-cabeza">
                  <button
                    type="button"
                    className="enlace-fila"
                    onClick={() => alAbrirAlumno(a.student_id)}
                  >
                    <strong>{nombreDeAlumno(a)}</strong>
                  </button>
                  {faltan.length === 0 ? (
                    <span className="pastilla pastilla-bien">Al día</span>
                  ) : (
                    <span className="pastilla pastilla-paused">
                      {faltan.length === 1
                        ? 'Falta 1 obligatorio'
                        : `Faltan ${faltan.length} obligatorios`}
                    </span>
                  )}
                </div>

                {/*
                  Los que faltan por su nombre, en la fila: es lo que hay que pedirle
                  a esa familia, y tenerlo aquí evita abrir el detalle de los cuatro.
                */}
                {faltan.length > 0 && (
                  <p className="tenue">
                    Falta que firmen: {faltan.map(nombreDe).join(', ')}.
                  </p>
                )}

                <div className="aviso-pie">
                  <button
                    type="button"
                    className="enlace-fila"
                    aria-expanded={desplegado}
                    onClick={() => setAbierto(desplegado ? null : a.student_id)}
                  >
                    {desplegado
                      ? 'Ocultar los ocho permisos'
                      : 'Ver los ocho permisos'}
                  </button>
                </div>

                {desplegado && (
                  <ul className="permisos">
                    {a.consents.map((c) => {
                      const estado = estadoDeConsentimiento(c.granted);
                      const pinta = ESTADOS_DE_CONSENTIMIENTO[estado];
                      const quien = nombreDeQuienFirmo(c);

                      return (
                        <li key={c.code}>
                          <span className={`pastilla ${pinta.clase}`}>
                            {pinta.etiqueta}
                          </span>
                          <span>
                            {c.label}
                            {c.is_required && (
                              <span className="tenue"> · obligatorio</span>
                            )}
                          </span>
                          {/*
                            **Sin rama para `revoked_at`**, y no es un olvido: en la
                            fila que llega aquí siempre es `null` por construcción. No
                            existe una operación de «retirar» —retirar es
                            `setConsent(false)`, que cierra la fila anterior y abre una
                            nueva con `granted: false`— y la consulta del servidor solo
                            devuelve la abierta. Una retirada se ve como un «No», que
                            es lo correcto: la familia contestó.

                            Escribí esa rama antes de comprobarlo, con un comentario
                            que afirmaba que «retirado gana sobre concedido». Era
                            código muerto defendido con seguridad, que es peor que
                            código muerto a secas.
                          */}
                          <span className="tenue">
                            {c.granted_at
                              ? `${fecha(c.granted_at)}${quien ? ` · ${quien}` : ''}`
                              : ''}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/*
        La única acción que cabe desde aquí. Sin ella la pantalla es una lista de
        deberes sin bolígrafo: se ve a quién le falta y no hay forma de pedírselo.
      */}
      {lista.data && lista.data.some((a) => a.missing_required.length > 0) && (
        <div className="acciones">
          <button type="button" onClick={alAvisar}>
            Mandarles un aviso
          </button>
        </div>
      )}
    </section>
  );
}
